import React, { useEffect } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { ContactSection } from '../components/ContactSection';
import { UtmCapture } from '../components/UtmCapture';
import { CorporateEventBuilderForm } from '../components/CorporateEventBuilderForm';
import { motion } from 'framer-motion';
import { Users, MapPin, CheckCircle2, ArrowLeft } from 'lucide-react';
import { teamBuildingActivities, teamBuildingExtras, teamBuildingSteps } from '../data/corporateTeamBuilding';
const customizationPoints = ['בוחרים את הפעילויות שמתאימות לקבוצה — ומדלגים על מה שלא', 'קובעים את הקצב: יום ספורטיבי, יום רגוע, או שילוב של השניים', 'מרכיבים את התפריט, שעות היום ולוח הזמנים לפי מה שנוח לכם'];
function scrollToBuilder() {
  document.getElementById('event-builder')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}
export function CorporateTeamBuildingPage() {
  useEffect(() => {
    document.title = "ימי גיבוש לחברות וארגונים | ביץ' קלאב מעגן";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'ימי גיבוש לחברות וארגונים על חוף הכנרת — סאפ, טיולי חתירה, נשימה ואמבטיית קרח, עיסויים, בריכה וארוחות. כל יום נבנה בהתאמה אישית לקבוצה.');
    const ogTags = [{
      property: 'og:title',
      content: "ימי גיבוש לחברות וארגונים | ביץ' קלאב מעגן"
    }, {
      property: 'og:description',
      content: 'יום גיבוש על הכנרת שנבנה מא׳ ועד ת׳ סביב הקבוצה שלכם — פעילויות מים, גוף-נפש, פינוקים ואוכל.'
    }, {
      property: 'og:type',
      content: 'website'
    }, {
      property: 'og:url',
      content: window.location.href
    }];
    ogTags.forEach(tag => {
      let metaTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', tag.property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', tag.content);
    });
  }, []);
  return <div className="min-h-screen bg-white" dir="rtl" data-id="element-996" data-mp="yzyw8ix9sirn">
      <UtmCapture data-id="element-997" data-mp="1drurhw9j6ji" />
      <Navigation data-id="element-998" data-mp="1e9y0yhcel0x" />

      {/* Hero */}
      <header className="relative bg-[#0A5F8F] text-white pt-32 pb-20 overflow-hidden" data-id="element-999" data-mp="1fcqhaubcviy">
        <div className="absolute inset-0 opacity-10" aria-hidden="true" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-1000" data-mp="6e5t2882b24c" />
        <div className="container mx-auto px-4 relative z-10" data-id="element-1001" data-mp="1mptczbkpmrh6">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-w-3xl" data-id="element-1002" data-mp="1romq32w34ggt">
            <span className="inline-flex items-center gap-2 bg-white/15 border border-white/25 px-4 py-2 rounded-full text-sm font-semibold mb-6" data-id="element-1003" data-mp="32fkftzg4v8ad">
              <Users className="w-4 h-4" data-id="element-1004" data-mp="2sjpsir0ysp19" />
              לחברות, ארגונים וצוותים
            </span>
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-[1.05]" data-id="element-1005" data-mp="2ohmebumwv6ca">
              ימי גיבוש
              <span className="block text-cyan-300" data-id="element-1006" data-mp="2jhzs4vp3ufrc">על חוף הכנרת</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-50 font-light leading-relaxed mb-4 max-w-2xl" data-id="element-1007" data-mp="2ek7vxphfg7hc">
              יום שלם שבנוי מא׳ ועד ת׳ סביב הקבוצה שלכם. אתם בוחרים את
              הפעילויות, את הקצב ואת האוכל — ואנחנו מרכיבים את היום המדויק
              עבורכם.
            </p>
            <p className="text-lg text-cyan-100 font-semibold mb-10" data-id="element-1008" data-mp="36kktc2vpwn0v">
              כל קבוצה מקבלת בדיוק את מה שמתאים לה.
            </p>

            <div className="flex flex-wrap items-center gap-4" data-id="element-1009" data-mp="3aklj2jo9okn8">
              <button type="button" onClick={scrollToBuilder} className="inline-flex items-center gap-2 bg-white text-[#0A5F8F] font-bold text-lg py-4 px-8 rounded-full shadow-xl hover:bg-cyan-50 transition-colors" data-id="element-1010" data-mp="2wmn4pe9y4o7q">
                בואו נבנה את היום שלכם
                <ArrowLeft className="w-5 h-5" data-id="element-1011" data-mp="2ro2fsb059qyl" />
              </button>
              <span className="inline-flex items-center gap-2 text-blue-100" data-id="element-1012" data-mp="2dvvyump63fy3">
                <MapPin className="w-5 h-5 text-cyan-300" data-id="element-1013" data-mp="2dtsyp9uaw6fn" />
                חוף מעגן, כנרת
              </span>
            </div>
          </motion.div>
        </div>
      </header>

      <main data-id="element-1014" data-mp="23y83pvnng6fe">
        {/* Personalization */}
        <section className="py-20 bg-white" data-id="element-1015" data-mp="28w9ja6lv9nm4">
          <div className="container mx-auto px-4" data-id="element-1016" data-mp="28w19wooyd0fo">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center max-w-6xl mx-auto" data-id="element-1017" data-mp="1pahld36542gq">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} data-id="element-1018" data-mp="1l9fjsfor3ebl">
                <h2 className="text-4xl md:text-5xl font-bold text-[#0A5F8F] mb-6 leading-tight" data-id="element-1019" data-mp="288k2kvsg4v3z">
                  אין אצלנו חבילה אחת לכולם
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed mb-6" data-id="element-1020" data-mp="2hzexeojrd311">
                  לכל קבוצה יש אופי משלה — צוות שרוצה אתגר ספורטיבי, צוות שצריך
                  יום של רוגע ואוויר, או קבוצה שרוצה קצת מהכול. לכן אנחנו לא
                  מוכרים תבנית מוכנה, אלא בונים איתכם את היום מההתחלה.
                </p>
                <ul className="space-y-4" data-id="element-1021" data-mp="2mzatm1m7rq61">
                  {customizationPoints.map(point => <li key={point} className="flex items-start gap-3" data-id="element-1022" data-mp="2u2hcssl76afg">
                      <CheckCircle2 className="w-6 h-6 text-cyan-500 shrink-0 mt-0.5" data-id="element-1023" data-mp="2z0so77r54szs" />
                      <span className="text-lg text-gray-700" data-id="element-1024" data-mp="2yyg9d3goiyqy">{point}</span>
                    </li>)}
                </ul>
              </motion.div>

              <motion.ol initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-blue-50 border border-blue-100 rounded-3xl p-8 md:p-10 space-y-8" data-id="element-1025" data-mp="38s6qtoee28x5">
                {teamBuildingSteps.map((step, index) => <li key={step.title} className="flex gap-5" data-id="element-1026" data-mp="1sddvu9py5i7l" data-mpi={index}>
                    <span className="w-10 h-10 rounded-full bg-[#0A5F8F] text-white font-bold flex items-center justify-center shrink-0" data-id="element-1027" data-mp="1xb702x7ow258">
                      {index + 1}
                    </span>
                    <div data-id="element-1028" data-mp="1l1hetob0gpvx">
                      <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-1029" data-mp="1l06anndnlg4a">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed" data-id="element-1030" data-mp="1g00dxssj13fc">
                        {step.description}
                      </p>
                    </div>
                  </li>)}
              </motion.ol>
            </div>
          </div>
        </section>

        {/* Activities */}
        <section className="py-20 bg-gray-50" data-id="element-1031" data-mp="23pma07dlr2j4">
          <div className="container mx-auto px-4" data-id="element-1032" data-mp="1yrt6jm3oommj">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="max-w-3xl mb-14" data-id="element-1033" data-mp="1yup2x5zvv38v">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A5F8F] mb-4" data-id="element-1034" data-mp="1828hlbqsjcq5">
                מתוך מה אפשר לבחור?
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed" data-id="element-1035" data-mp="1d35v3b0uvilb">
                אלה הפעילויות שאנחנו מפעילים במתחם. בוחרים את מה שמדבר לקבוצה
                שלכם, בכל שילוב שתרצו.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1036" data-mp="135896u6gxxvv">
              {teamBuildingActivities.map((activity, index) => <motion.article key={activity.id} initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: Math.min(index * 0.05, 0.25)
            }} className="bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow overflow-hidden flex flex-col h-full" data-id="element-1037" data-mp="1vr17orkqeqcu" data-mpi={index}>
                  {activity.images && activity.images.length > 0 ? <div className="bg-blue-50 flex flex-col sm:flex-row sm:h-64 items-stretch gap-2 p-2" data-id="element-1038" data-mp="csmozbhl9uv7">
                      {activity.images.map(src => <div key={src} className="flex-1 min-w-0 min-h-0 rounded-xl overflow-hidden" data-id="element-1039" data-mp="3gb6alv5ri2h1">
                          <img src={src} alt={activity.title} className="block w-full h-auto sm:h-full object-contain object-center" data-id="element-1040" data-mp="3q8u5qm7a5bzq" />
                        </div>)}
                    </div> : <div className="h-24 bg-blue-50 flex items-center px-8" data-id="element-1041" data-mp="10ipfvo9rous8">
                      <span className="w-14 h-14 rounded-2xl bg-[#0A5F8F] flex items-center justify-center shrink-0" data-id="element-1042" data-mp="3hon9axnjev8g">
                        <activity.icon className="w-7 h-7 text-white" data-id="element-1043" data-mp="3crnfbl3ypixp" />
                      </span>
                    </div>}
                  <div className="p-8 flex flex-col flex-1" data-id="element-1044" data-mp="331bwytjj777a">
                    <h3 className="text-xl font-bold text-gray-900 mb-1.5 leading-snug" data-id="element-1045" data-mp="330sixnx052yj">
                      {activity.title}
                    </h3>
                    <p className="text-sm font-semibold text-cyan-600 mb-4" data-id="element-1046" data-mp="2u04cpfduo6w7">
                      {activity.tagline}
                    </p>
                    <p className="text-gray-600 leading-relaxed" data-id="element-1047" data-mp="2p1jaxbiy9f5n">
                      {activity.description}
                    </p>
                  </div>
                </motion.article>)}
            </div>
          </div>
        </section>

        {/* Food and extras */}
        <section className="py-20 bg-white" data-id="element-1048" data-mp="ibcs4sxs9e8w">
          <div className="container mx-auto px-4" data-id="element-1049" data-mp="pum32qlxtl02">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start" data-id="element-1050" data-mp="ptt3qapb5xry">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} data-id="element-1051" data-mp="usdvdlqhv94s">
                <h2 className="text-4xl md:text-5xl font-bold text-[#0A5F8F] mb-4" data-id="element-1052" data-mp="6xb4l884qvlo">
                  אוכל, שתייה ופינוקים
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed mb-10" data-id="element-1053" data-mp="gtxnvskt5ell">
                  אפשר להזמין ארוחות ממגוון תפריטים ולהוסיף כל מה שהופך את היום
                  לחגיגה.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1054" data-mp="1s9ckgv93u4g6">
                  {teamBuildingExtras.map((extra, index) => <motion.div key={extra.title} initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: Math.min(index * 0.05, 0.2)
                }} className="border-t-2 border-cyan-500 pt-5" data-id="element-1055" data-mp="1x6l7pa940yoa" data-mpi={index}>
                      <extra.icon className="w-7 h-7 text-[#0A5F8F] mb-3" data-id="element-1056" data-mp="35jdhgnb6a3uj" />
                      <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1057" data-mp="3jefrnmn1yfd5">
                        {extra.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed" data-id="element-1058" data-mp="2qwk5ns0v3g73">
                        {extra.description}
                      </p>
                    </motion.div>)}
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1059" data-mp="2cq0eqb4mkwij">
                <img src="https://cdn.magicpatterns.com/uploads/prmJwAT8u35bpB2JwS8Yqu/EliteHenensonPhoto_20260612_Sup_Club_Maagan_363.jpg" alt="מרימים כוסית סביב שולחן האוכל במתחם" className="sm:col-span-2 block w-full h-auto rounded-2xl" data-id="element-1060" data-mp="2vh9ngi6knh92" />
                <img src="https://cdn.magicpatterns.com/uploads/uiWKpHUHiwYM9djQcYMA9f/EliteHenensonPhoto_20260612_Sup_Club_Maagan_352.jpg" alt="מגש כריכים טריים לקבוצה" className="block w-full h-auto rounded-2xl" data-id="element-1061" data-mp="2qh57qudhmqjw" />
                <img src="https://cdn.magicpatterns.com/uploads/25RUWhTYYvZktcFSSQd9Cu/EliteHenensonPhoto_20260612_Sup_Club_Maagan_351.jpg" alt="מגש קישים במגוון טעמים" className="block w-full h-auto rounded-2xl" data-id="element-1062" data-mp="1vao70v13qzi5" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Event builder */}
        <section id="event-builder" className="py-20 bg-blue-50 scroll-mt-24" data-id="element-1063" data-mp="2e9jxbv9uatqp">
          <div className="container mx-auto px-4" data-id="element-1064" data-mp="29agr47i4ihh5">
            <div className="max-w-3xl mx-auto" data-id="element-1065" data-mp="29aqgptz9ammb">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="text-center mb-10" data-id="element-1066" data-mp="24auf21e1779x">
                <h2 className="text-4xl md:text-5xl font-bold text-[#0A5F8F] mb-4" data-id="element-1067" data-mp="nyyv46qd073b">
                  בואו נבנה את היום המושלם שלכם
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed" data-id="element-1068" data-mp="34kc85qwq1yfo">
                  השאירו פרטים וספרו לנו מה מעניין אתכם — נחזור אליכם עם הצעה
                  שמותאמת בדיוק לקבוצה שלכם.
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-white rounded-3xl shadow-xl border border-blue-100 p-6 md:p-10" data-id="element-1069" data-mp="3eb6uswdvr01n">
                <CorporateEventBuilderForm data-id="element-1070" data-mp="1xuu7gsqic7ty" />
              </motion.div>
            </div>
          </div>
        </section>
      </main>

      <ContactSection showTeam={false} data-id="element-1071" data-mp="22t5iv7wgaqea" />
      <Footer data-id="element-1072" data-mp="22u71pyjpkh6k" />
    </div>;
}